import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import {
    Modal,
    ModalBodyFooter,
    ModalProcessing,
    Button,
    ErrorMessage
} from '../../../../components/custom-essentials';
import { FormikControl, Check } from '../../../../components/form';

const validationSchema = Yup.object().shape({
    title: Yup.string().trim().required('Title is required'),
    author: Yup.string().trim().required('Author is required')
});

function BookModal(props) {
    const {
        show = true,
        mode = 'create',
        book,
        onClose,
        createBook,
        updateBook,
        deleteBook
    } = props;

    const [submitted, setSubmitted] = useState(false);
    const [submissionStatus, setSubmissionStatus] = useState(null);
    const [deleteProcessing, setDeleteProcessing] = useState(false);
    const [deleteStatus, setDeleteStatus] = useState(null);

    const initialValues = {
        title: book?.title || '',
        author: book?.author || '',
        difficulty: book?.difficulty || '',
        theme: book?.theme || '',
        type: book?.type || '',
        notes: book?.notes || '',
        contentWarning: book?.content_warning || '',
        active: parseInt(book?.active ?? 1) === 1
    };

    const closeModal = (shouldRefresh = false) => {
        if (onClose) onClose(shouldRefresh);
    };

    const handleSubmit = async (values, formikHelpers) => {
        const { setSubmitting, setStatus } = formikHelpers;
        setStatus(null);

        const payload = {
            title: values.title.trim(),
            author: values.author.trim(),
            difficulty: values.difficulty.trim(),
            theme: values.theme.trim(),
            type: values.type.trim(),
            notes: values.notes.trim(),
            contentWarning: values.contentWarning.trim(),
            active: values.active ? 1 : 0
        };

        try {
            let response = null;
            if (mode === 'edit') {
                response = await updateBook({ ...payload, id: book?.id });
            } else {
                response = await createBook(payload);
            }

            if (!response || response.status >= 400) {
                setStatus(response?.data?.message || 'Unable to save book. Please try again.');
                setSubmitting(false);
                return;
            }

            setSubmitting(false);
            setSubmitted(true);
            setSubmissionStatus(mode === 'edit' ? 'updated' : 'created');
        } catch (error) {
            console.error('[BOOK MODAL] Error saving book:', error);
            setStatus('Unexpected error while saving the book.');
            setSubmitting(false);
        }
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues,
        validationSchema,
        onSubmit: handleSubmit
    });

    const modalTitle = mode === 'edit'
        ? `Edit Book${book?.title ? ` - ${book.title}` : ''}`
        : mode === 'delete'
            ? `Delete Book${book?.title ? ` - ${book.title}` : ''}`
            : 'Add Book';

    const handleDelete = async () => {
        if (!book?.id) {
            setDeleteStatus('Missing book ID.');
            return;
        }
        setDeleteProcessing(true);
        setDeleteStatus(null);
        try {
            const response = await deleteBook({ id: book.id });
            if (!response || response.status >= 400) {
                setDeleteStatus(response?.data?.message || 'Unable to delete book.');
                setDeleteProcessing(false);
                return;
            }
            setDeleteProcessing(false);
            setSubmitted(true);
            setSubmissionStatus('deleted');
        } catch (error) {
            console.error('[BOOK MODAL] Error deleting book:', error);
            setDeleteProcessing(false);
            setDeleteStatus('Unexpected error while deleting the book.');
        }
    };

    const renderSuccess = () => (
        <ModalBodyFooter>
            <Modal.Body>
                <h4 className="mb-2 text-mpBlue">
                    {submissionStatus === 'deleted'
                        ? 'Book deleted successfully.'
                        : `Book ${submissionStatus} successfully.`}
                </h4>
                <div>You can close this dialog to refresh the latest list.</div>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    color="mpBlue"
                    onClick={() => closeModal(true)}
                >
                    Close
                </Button>
            </Modal.Footer>
        </ModalBodyFooter>
    );

    const renderDelete = () => (
        <ModalBodyFooter>
            <Modal.Body>
                <h4 className="text-mpLRed mb-2">Delete this book?</h4>
                <p>
                    This action cannot be undone.
                    {book?.title ? ` (${book.title})` : ''}
                </p>
                {deleteStatus && (
                    <>
                        <br />
                        <ErrorMessage color="mpLRed">{deleteStatus}</ErrorMessage>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <div className="flex flex-row gap-x-2 flex-wrap items-center">
                    {deleteProcessing && <ModalProcessing />}
                    <Button
                        color="mpLRed"
                        onClick={() => closeModal(false)}
                        disabled={deleteProcessing}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="hol-mpLRed"
                        onClick={handleDelete}
                        disabled={deleteProcessing}
                    >
                        Delete
                    </Button>
                </div>
            </Modal.Footer>
        </ModalBodyFooter>
    );

    const renderForm = () => (
        <ModalBodyFooter>
            <Modal.Body>
                <div className="grid grid-cols-1 gap-y-4">
                    <div className="grid grid-cols-1 gap-y-2">
                        <FormikControl
                            id="book-title"
                            name="title"
                            label="Title"
                            placeholder="Enter book title"
                            value={formik.values.title}
                            onChange={formik.handleChange}
                        />
                        {formik.errors.title && (
                            <ErrorMessage color="mpLRed">
                                {formik.errors.title}
                            </ErrorMessage>
                        )}
                    </div>
                    <div className="grid grid-cols-1 gap-y-2">
                        <FormikControl
                            id="book-author"
                            name="author"
                            label="Author"
                            placeholder="Enter author name"
                            value={formik.values.author}
                            onChange={formik.handleChange}
                        />
                        {formik.errors.author && (
                            <ErrorMessage color="mpLRed">
                                {formik.errors.author}
                            </ErrorMessage>
                        )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <FormikControl
                            id="book-difficulty"
                            name="difficulty"
                            label="Difficulty"
                            placeholder="e.g. Level 3"
                            value={formik.values.difficulty}
                            onChange={formik.handleChange}
                        />
                        <FormikControl
                            id="book-theme"
                            name="theme"
                            label="Theme"
                            placeholder="e.g. Adventure"
                            value={formik.values.theme}
                            onChange={formik.handleChange}
                        />
                        <FormikControl
                            id="book-type"
                            name="type"
                            label="Type"
                            placeholder="e.g. Fiction"
                            value={formik.values.type}
                            onChange={formik.handleChange}
                        />
                    </div>
                    <div className="grid grid-cols-1 gap-y-2">
                        <FormikControl
                            id="book-notes"
                            name="notes"
                            as="textarea"
                            rows="3"
                            label="Notes"
                            placeholder="Internal notes..."
                            value={formik.values.notes}
                            onChange={formik.handleChange}
                        />
                    </div>
                    <div className="grid grid-cols-1 gap-y-2">
                        <FormikControl
                            id="book-content-warning"
                            name="contentWarning"
                            as="textarea"
                            rows="2"
                            label="Content Warning"
                            placeholder="Optional content warning..."
                            value={formik.values.contentWarning}
                            onChange={formik.handleChange}
                        />
                    </div>
                    <div>
                        <Check
                            color="mpBlue"
                            name="active"
                            label="Book is active"
                            checked={formik.values.active}
                            onChange={formik.handleChange}
                        />
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <div className="flex flex-row gap-x-2 flex-wrap items-center">
                    {formik.isSubmitting && <ModalProcessing />}
                    {formik.status && (
                        <ErrorMessage color="mpLRed">
                            {formik.status}
                        </ErrorMessage>
                    )}
                    <Button
                        color="mpLRed"
                        onClick={() => closeModal(false)}
                        disabled={formik.isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="mpBlue"
                        onClick={formik.handleSubmit}
                        disabled={formik.isSubmitting}
                    >
                        {mode === 'edit' ? 'Save Changes' : 'Add Book'}
                    </Button>
                </div>
            </Modal.Footer>
        </ModalBodyFooter>
    );

    // Determine which content to show - must be after all render functions are defined
    const modalContent = submitted
        ? renderSuccess()
        : mode === 'delete'
            ? renderDelete()
            : renderForm();

    return (
        <Modal
            show={show}
            onHide={() => closeModal(false)}
            preventClickout={false}
            className="w-1/2"
        >
            <Modal.Header>
                <h2>{modalTitle}</h2>
            </Modal.Header>
            {modalContent}
        </Modal>
    );
}

export default BookModal;


